import { ExampleDetail } from '../../models/example-detail.model';
import { Validators, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { DataAction } from '../../../enum/data-action.enum';
import { ExampleService } from '../../services/example.service';
import { Subject, Observable, takeUntil, of, lastValueFrom, switchMap, map } from 'rxjs';
import { ExampleData } from '../../models/example-data.model';
import { Option } from '../../../models/option.model';
import { ExampleMaster } from '../../models/example-master.model';
import { Component, ViewChild } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
import { TabMenu } from 'primeng/tabmenu';
import { TableSelectionMode } from '../../../enum/table-selection-mode.enum';
import { SimpleDoubleInlineEditComponent } from '../../../components/abstract/simple/simple-double-inline-edit.component';

@Component({
  selector: 'app-example-inline-edit',
  templateUrl: './example-inline-edit.component.html',
  styleUrls: ['./example-inline-edit.component.scss']
})
export class ExampleInlineEditComponent extends SimpleDoubleInlineEditComponent {

  // single or multiple
  override tableSelectionMode: 'single' | 'multiple' = TableSelectionMode.MULTIPLE;

  // Component 的 class 名稱
  override componentClassName: string = 'ExampleInlineEditComponent';

  // Table 資料的 key 欄位名稱
  override rowDataKeyName: string = 'uuid';

  // Table 資料刪除時要取哪個欄位的值來顯示提示
  override rowDataDeleteHintFieldName: string = 'detailInput';

  // 為了寫程式可以點出提示，override 這個自行定義資料型別
  override masterData: ExampleMaster | undefined;

  // 為了寫程式可以點出提示，override 這個自行定義資料型別
  override detailDatas: ExampleDetail[] = [];

  // 為了寫程式可以點出提示，override 這個自行定義資料型別
  override rowCurrentData: ExampleDetail | undefined;

  // 有 filter 功能
  override hasTableFilter: boolean = true;

  // 取得 Table 分頁頁籤物件，後面用來抓頁籤資訊用的
  @ViewChild('detailTabMenuComponent') override detailTabMenuComponent!: TabMenu;

  // 顯示單身批次刪除的按鈕
  // 不設定 rowDeleteChangeToClear = true 是因為 inline edit 一定都是 clear，父類別設好了
  override canBatchDeleteRows: boolean = true;

  // 下拉選單的 Options 資料
  exampleOptions: Option[] = [];

  // 動態下拉選單的 Options 資料
  dynamicOptions: Option[] = [];

  // 自動完成的 Suggestions 資料
  exampleDataSuggestions: ExampleData[] = [];
  // 自動完成欄位值變動的 Subject，用來避免前面一次查詢比較慢返回造成覆蓋資料
  private exampleDataSubject$ = new Subject<void>();

  // 自動完成 + 下拉的 Suggestions 資料
  allExampleData?: ExampleData[];
  // 自動完成 + 下拉的 Suggestions 資料
  allExampleDataSuggestions: ExampleData[] = [];
  // 自動完成 + 下拉欄位值變動的 Subject，用來避免前面一次查詢比較慢返回造成覆蓋資料
  private allExampleDataSubject$ = new Subject<void>();

  constructor(private exampleService: ExampleService) {
    super();
  }

  override initFormGroup(): void {
    // 設定單頭 formGroup 內容
    this.initMasterFormGroup();
    // 設定單身 formGroup 內容
    this.initDetailFormGroup();
  }

  private initMasterFormGroup(): void {
    this.formGroup = this.formBuilder.nonNullable.group({
      id: '',
      uuid: '',
      input: ['', Validators.required],
      calendar: ['', Validators.required],
      dropdown: '',
      autoComplete: '',
      inputNumber: '',
      calendarRange: '',
      custValidation: ['', this.custValidator()],
      dynamicDropdown: '',
      autoCompleteDropdown: ''
    });
  }

  private initDetailFormGroup(): void {
    this.inlineFormGroup = this.formBuilder.nonNullable.group({
      id: '',
      uuid: '',
      detailInput: ['', Validators.required],
      detailCalendar: ['', Validators.required],
      detailDropdown: ['', Validators.required],
      detailAutoCompleteCode: '',
      detailInputNumber: null,
      detailCalendarRange: '',
      detailCustValidation: ['', this.detailCustValidator()],
      detailDynamicDropdown: '',
      detailAutoCompleteDropdownCode: ''
    });
  }

  override initFormControlDisabled(): void {
    // 如果單頭表單欄位沒有特別需要控制不可編輯就不用寫
    return;
  }

  override initStaticOptions(): void {
    // 下拉選單選項
    this.exampleOptions = [
      {
        id: 'option.yes',
        label: this.translateService.instant('option.yes'),
        value: 'Y'
      },
      {
        id: 'option.no',
        label: this.translateService.instant('option.no'),
        value: 'N'
      }
    ];
  }

  override async initFormData(): Promise<any> {
    // 抓取 Master Data
    this.masterData = await lastValueFrom(this.loadMasterData());

    this.formGroup.patchValue({
      id: this.masterData?.id,
      uuid: this.masterData?.uuid,
      input: this.masterData?.input,
      calendar: this.masterData?.calendar,
      dropdown: this.masterData?.dropdown,
      inputNumber: this.masterData?.inputNumber,
      custValidation: this.masterData?.custValidation,
      dynamicDropdown: this.masterData?.dynamicDropdown
    });

    console.log('setMasterData = ' + JSON.stringify(this.formGroup.getRawValue()));

    await this.setDynamicOptions();
    await Promise.all([this.patchAutoComplete(), this.patchAutoCompleteDropdown()]);

    // 抓 Detail 資料
    this.detailDatas = await lastValueFrom(this.loadDetailData());
  }

  override afterFormDataLoadSuccess(): void {
    return;
  }

  override afterFormDataLoadFail(): void {
    return;
  }

  /**
   * 這裡應該是去取 uuid 重新查資料，或是直接拿前面傳入的資料來顯示，要自己判斷處理。
   * 為了統一處理的行為，建議都重新取資料比較一致，且資料會是最新的資料
   *
   * @returns
   */
  loadMasterData(): Observable<ExampleMaster> {
    console.log('ExampleDoubleComponent loadMasterData isDialogMode = ' + this.isDialogMode);

    // 為了統一處理的行為，建議都重新取資料比較一致，且資料會是最新的資料
    if (this.isDialogMode) {

      // 以下取 masterDataKeyValue 在 2023/08/13 取消，改在 BaseFormComponent 的 ngOnInit 就先取回。

      // 取出查詢 Master Data 的 Key
      // const data: ExampleMaster = this.dynamicDialogConfig.data;
      // const dataAction: DataAction = this.dynamicDialogConfig.data?.dataAction;

      // console.log('ExampleDoubleComponent loadMasterData dynamicDialogConfig = ' + JSON.stringify(this.dynamicDialogConfig));
      // console.log('ExampleDoubleComponent loadMasterData dataAction = ' + dataAction);

      // this.masterDataKeyValue = this.dynamicDialogConfig.data[this.masterDataKeyName];
    } else {
      this.masterDataKeyValue = this.activatedRoute.snapshot.paramMap.get(this.masterDataKeyName);
    }

    console.log('ExampleDoubleComponent loadMasterData uuid = ' + this.masterDataKeyValue);
    // 如果前面是做新增，而且沒有傳 key 的值，就產生一個空的
    if (this.parentDataAction == DataAction.NEW && !this.masterDataKeyValue) {
      let exampleMaster = new ExampleMaster();
      exampleMaster.id = DataAction.NEW;
      exampleMaster.uuid = uuidv4();
      return of(exampleMaster);
    } else {
      return this.exampleService.getExampleMasterData(this.masterDataKeyValue!).pipe(
        takeUntil(this._destroying$)
      );
    }
  }

  /**
   * 建議這些要在程式一開始前就取回來的東西分不同 function 處理
   */
  async setDynamicOptions(): Promise<void> {
    console.log('setDynamicOptions start');
    this.dynamicOptions = await lastValueFrom(
      this.exampleService.queryAllExampleDataOptions().pipe(takeUntil(this._destroying$))
    );
    console.log('setDynamicOptions end');
  }

  /**
   * 欄位只有存 key 值的，去串回他的物件。
   * 也可以跟後端先講好他回傳資料的時候就拚好，就不用額外去呼叫取得。
   */
  async patchAutoComplete(): Promise<void> {
    console.log('patchAutoComplete start');
    const keyword = this.masterData?.autoCompleteCode;
    if (keyword) {
      const data: ExampleData[] = await lastValueFrom(
        this.exampleService.queryExampleDatas(keyword).pipe(takeUntil(this._destroying$))
      );

      if (data.length > 0) {
        this.formGroup.patchValue({
          autoComplete: data.find(exampleData => exampleData.exampleDataCode == keyword)
        });
      } else {
        throw new Error('AutoComplete data ' + keyword + ' not found');
      }
    }
  }

  /**
   * 欄位只有存 key 值的，去串回他的物件。
   * 也可以跟後端先講好他回傳資料的時候就拚好，就不用額外去呼叫取得。
   */
  async patchAutoCompleteDropdown(): Promise<void> {
    console.log('patchAutoCompleteDropdown start');
    const keyword = this.masterData?.autoCompleteDropdownCode;
    if (keyword) {
      const data: ExampleData[] = await lastValueFrom(
        this.exampleService.queryExampleDatas(keyword).pipe(takeUntil(this._destroying$))
      );

      if (data.length > 0) {
        this.formGroup.patchValue({
          autoCompleteDropdown: data.find(exampleData => exampleData.exampleDataCode == keyword)
        });
      } else {
        throw new Error('AutoCompleteDropdown data ' + keyword + ' not found');
      }
    }
  }

  /**
   * 這裡應該是去取 uuid 重新查資料，或是直接拿前面傳入的資料來顯示，要自己判斷處理。
   * 為了統一處理的行為，建議都重新取資料比較一致，且資料會是最新的資料
   *
   * @returns
   */
  loadDetailData(): Observable<ExampleDetail[]> {
    // 因為單頭的 Key 值在 loadMasterData 的時候就取過了，這邊可以直接用
    console.log('ExampleDoubleComponent loadMasterData uuid = ' + this.masterDataKeyValue);
    // 如果前面是做新增，而且沒有傳 key 的值，就產生一個空的
    if (this.parentDataAction == DataAction.NEW && !this.masterDataKeyValue) {
      return of([]);
    } else {
      // 這裡應該傳 parentUuid 去查，因為傳 id 隨便人都可以改數字就抓到資料，
      // 這裡傳 id 只是因為我沒有後端程式可以處理，只是為了做取值範例
      return this.exampleService.getExampleDetailData(this.masterData?.id!).pipe(
        takeUntil(this._destroying$)
      );
    }
  }

  override initExtraMasterMenu(): void {
    this.masterMenu.push(
      {
        id: this.parseToId('btnMasterMenuOther'),
        label: '其他功能按鈕',
        automationId: '其他功能按鈕',
        command: () => {
          // 這裡應該去呼叫獨立寫的一個 function 做事情
          this.systemMessageService.info('按下其他功能按鈕');
        }
      },
      {
        id: this.parseToId('btnMasterMenuNoIcon'),
        label: '如果不能給有意義的 icon 就只放文字就好',
        command: () => {
          // 這裡應該去呼叫獨立寫的一個 function 做事情
          this.systemMessageService.info('按下如果不能給有意義的 icon 就只放文字就好');
        }
      }
    );
  }

  override setExtraMasterMenuStatus(): void {
    let masterMenuButtonOther = this.masterMenu.find(menuItem => menuItem.id == this.parseToId('btnMasterMenuOther'));
    if (masterMenuButtonOther) {
      masterMenuButtonOther.disabled = !this.masterInViewStatus();
    }

    let masterMenuButtonNoIcon = this.masterMenu.find(menuItem => menuItem.id == this.parseToId('btnMasterMenuNoIcon'));
    if (masterMenuButtonNoIcon) {
      masterMenuButtonNoIcon.disabled = !this.masterInViewStatus();
    }
  }

  override afterLangChange(): void {
    this.languageService.processOptionTranslation(this.exampleOptions);
  }

  override initExtraDetailMenu(): void {
    this.detailMenu.push(
      {
        id: this.parseToId('btnDetailMenuSomeAction'),
        label: '選擇後的動作',
        automationId: '選擇後的動作',
        command: () => {
          this.someAction();
        }
      }
    );
    return;
  }

  override setExtraDetailMenuStatus(): void {
    let detailMenuButtonOther = this.detailMenu.find(menuItem => menuItem.id == this.parseToId('btnDetailMenuSomeAction'));
    if (detailMenuButtonOther) {
      detailMenuButtonOther.disabled = !this.rowSelectedDatas || this.rowSelectedDatas.length <= 0;
    }
  }

  override initTableColumns(): void {
    // automationId 其實設計是用在自動化測試工具可以通過這個識別符號來識別和操作這個元素。
    // 但維運 Team 測試是用 id，為了做到語系變更可以即時切換顯示，所以透過不會用到的 automationId 來設定 i18n 的 Key

    // 先定義頁籤
    this.detailTabs = [
      {
        id: this.parseToId('detailTab1'),
        label: this.translateService.instant('Tab 1'),
        automationId: 'Tab 1'
      },
      {
        id: this.parseToId('detailTab2'),
        label: this.translateService.instant('Tab 2'),
        automationId: 'Tab 2'
      },
      {
        id: this.parseToId('detailTab3'),
        label: this.translateService.instant('Tab 3'),
        automationId: 'Tab 3'
      }
    ];
    this.detailTabMenuComponent.activeItem = this.detailTabs[0];

    // 設定頁籤 detailTab1 內的欄位
    this.detailTabColumns[this.parseToId('detailTab1')] = [
      {
        header: '文字輸入',
        field: 'detailInput',
        required: true,
        sortable: true,
        filter: {
          type: 'text'
        }
      },
      {
        header: '選擇日期',
        field: 'detailCalendar',
        required: true,
        sortable: false,
        filter: {
          type: 'date',
          matchMode: 'dateIs'
        }
      },
      {
        header: '下拉選單',
        field: 'detailDropdown',
        required: true,
        translateArrayValue: {
          datas: this.exampleOptions,
          valueField: 'value',
          displayField: 'label'
        },
        sortable: false,
        filter: {
          type: 'dropdown',
          options: this.exampleOptions,
          matchMode: 'equals'
        }
      },
      {
        header: '自動完成',
        field: 'detailAutoCompleteName',
        inlineFormControlName: 'detailAutoCompleteCode',
        translateDataValueField: 'detailAutoCompleteName',
        sortable: false,
        filter: {
          type: 'text',
          field: 'detailAutoCompleteFilter'
        }
      }
    ];

    // 設定頁籤 detailTab2 內的欄位
    this.detailTabColumns[this.parseToId('detailTab2')] = [
      {
        header: '數字輸入',
        field: 'detailInputNumber',
        numberField: true,
        sortable: true,
        filter: {
          type: 'numeric',
          matchMode: 'equals'
        }
      },
      {
        header: '動態下拉',
        field: 'detailDynamicDropdown',
        translateArrayValue: {
          datas: this.dynamicOptions,
          valueField: 'value',
          displayField: 'label'
        },
        sortable: true,
        filter: {
          type: 'dropdown',
          options: this.dynamicOptions,
          matchMode: 'equals',
          translateOptionFieldName: true
        }
      }
    ];

    // 設定頁籤 detailTab3 內的欄位
    this.detailTabColumns[this.parseToId('detailTab3')] = [
      {
        header: '自動完成+下拉',
        field: 'detailAutoCompleteDropdownName',
        inlineFormControlName: 'detailAutoCompleteDropdownCode',
        translateDataValueField: 'detailAutoCompleteDropdownName',
        sortable: true,
        filter: {
          type: 'text'
        }
      },
      {
        header: '自訂欄位驗證',
        field: 'detailCustValidation',
        filter: {
          type: 'text'
        },
        width: 350,
        overflowBreak: true
      }
    ];

    // 把所有頁籤的欄位都設定到 Table Column 內
    this.tableColumns = [
      ...this.detailTabColumns[this.parseToId('detailTab1')],
      ...this.detailTabColumns[this.parseToId('detailTab2')],
      ...this.detailTabColumns[this.parseToId('detailTab3')]
    ];
    // console.log('tableColumns = ' + JSON.stringify(this.tableColumns));

    // 設定目前顯示第一個頁籤的欄位
    this.selectedColumns = this.detailTabColumns[this.parseToId('detailTab1')];
  }

  override afterSelectedRowDataCountChange(selectedDataCount: number): void {
    // 控制當有選取資料的時候按鈕才能按
    let menuItem = this.detailMenu.find(menuItem => menuItem.id == this.parseToId('btnDetailMenuSomeAction'));
    if (menuItem) {
      menuItem.disabled = selectedDataCount <= 0;
    }
    this.redrawDetailMenu();
  }

  override afterRowDataCountChange(rowDataCount: number): void {
    return;
  }

  override afterDetailTabActiveItemChange(): void {
    return;
  }

  override async doMasterMenuSubmit(): Promise<boolean> {
    return new Promise(resolve => {
      // TODO 實作存檔時要呼叫 API 存入資料，成功後記得要呼叫傳入的 callback function
      // setTimeout 是故意模擬處理的時間，實際上不會這樣做
      setTimeout(() => {
        let submitData: ExampleMaster = new ExampleMaster();

        // 存檔後如果 id 是 new 表示新增的資料，把 id 清空，之後才不會被判斷成是新增未存檔的資料
        submitData.id = this.formGroup.getRawValue().id != DataAction.NEW ? this.formGroup.getRawValue().id : '';
        submitData.uuid = this.formGroup.getRawValue().uuid;
        submitData.input = this.formGroup.getRawValue().input;
        submitData.calendar = this.formGroup.getRawValue().calendar;
        submitData.dropdown = this.formGroup.getRawValue().dropdown;
        submitData.autoCompleteCode = this.formGroup.getRawValue().autoComplete?.exampleDataCode;
        submitData.autoCompleteName = this.formGroup.getRawValue().autoComplete?.exampleDataName;
        submitData.autoCompleteNameTw = this.formGroup.getRawValue().autoComplete?.exampleDataNameTw;
        submitData.autoCompleteNameCn = this.formGroup.getRawValue().autoComplete?.exampleDataNameCn;
        submitData.autoCompleteNameUs = this.formGroup.getRawValue().autoComplete?.exampleDataNameUs;
        submitData.inputNumber = this.formGroup.getRawValue().inputNumber;
        submitData.calendarRange = this.formGroup.getRawValue().calendarRange;
        submitData.custValidation = this.formGroup.getRawValue().custValidation;
        submitData.dynamicDropdown = this.formGroup.getRawValue().dynamicDropdown;
        submitData.autoCompleteDropdownCode = this.formGroup.getRawValue().autoCompleteDropdown?.exampleDataCode;
        submitData.autoCompleteDropdownName = this.formGroup.getRawValue().autoCompleteDropdown?.exampleDataName;
        submitData.autoCompleteDropdownNameTw = this.formGroup.getRawValue().autoCompleteDropdown?.exampleDataNameTw;
        submitData.autoCompleteDropdownNameCn = this.formGroup.getRawValue().autoCompleteDropdown?.exampleDataNameCn;
        submitData.autoCompleteDropdownNameUs = this.formGroup.getRawValue().autoCompleteDropdown?.exampleDataNameUs;

        console.log('submitData = ' + JSON.stringify(submitData));

        // 單身的資料應可以直接從 this.detailDatas 取得
        console.log('submitData detailDatas = ' + JSON.stringify(this.detailDatas));

        this.systemMessageService.info('模擬實際存檔會花一點時間處理');
        // 儲存資料的時候要把資料也更新回 masterData

        // 儲存完資料後端應該要回傳儲存後的物件，然後要把它更新至 this.masterData
        // 因為有可能會儲存完又編輯，這時候會直接用 masterData 來維護
        this.masterData = submitData;

        resolve(true);
      }, 1000);
    });
  }

  override async doMasterMenuDelete(): Promise<boolean> {
    return new Promise(resolve => {
      // TODO 實作刪除時要呼叫 API 刪除資料，成功後記得要呼叫傳入的 callback function
      // setTimeout 是故意模擬處理的時間，實際上不會這樣做
      setTimeout(() => {
        this.systemMessageService.info('模擬實際刪除會花一點時間處理');
        resolve(true);
      }, 1000);
    });
  }

  custValidator(): ValidatorFn {
    return (formControl: AbstractControl): ValidationErrors | null => {
      if (!formControl.parent) {
        return null;
      }

      // 欄位要做什麼驗證
      const error = formControl.parent?.get('custValidation')?.value == '1';

      // 如果有錯
      if (error) {
        // 回傳 ValidationErrors，duplicate 是 error 的 key
        return { duplicate: true };
      }
      // 驗證結果符合需求，回傳 null
      return null;
    }
  }

  searchExampleDataSuggestions(event: any) {
    // 這個訂閱不寫在最上面是希望讓自動完成欄位的程式集中在一起，比較好閱讀
    if (!this.exampleDataSubject$.observed) {
      this.exampleDataSubject$.pipe(
        switchMap(keyword => this.exampleService.queryExampleDatas(keyword!)),
        takeUntil(this._destroying$)
      ).subscribe(data => {
        this.exampleDataSuggestions = data;
        console.log('this.exampleDataSuggestions = ' + this.exampleDataSuggestions.length);
      });
    }

    this.exampleDataSubject$.next(event.query);
  }

  searchAllExampleDataSuggestions(event: any) {
    // 這個訂閱不寫在最上面是希望讓自動完成欄位的程式集中在一起，比較好閱讀
    if (!this.allExampleDataSubject$.observed) {
      this.allExampleDataSubject$.pipe(
        switchMap(keyword => {
          // 如果 allExampleData 已經有取過值了就用它，不然就呼叫 API 取
          let data: Observable<ExampleData[]> = this.allExampleData ? of(this.allExampleData) : this.exampleService.queryAllExampleDatas()
          return data.pipe(
            map(data => {
              // 把回傳所有的值塞到 allExampleData
              this.allExampleData = data;
              // 根據輸入的關鍵字過濾
              return data.filter(exampleData =>
                exampleData.exampleDataName.indexOf(keyword!) >= 0
                || exampleData.exampleDataNameCn.indexOf(keyword!) >= 0
                || exampleData.exampleDataNameUs.indexOf(keyword!) >= 0)
            })
          );
        }),
        takeUntil(this._destroying$)
      ).subscribe(data => {
        this.allExampleDataSuggestions = data;
      });
    }

    this.allExampleDataSubject$.next(event.query);
  }

  someAction(): void {
    // 這裡是為了寫單選和複選的使用例子，實際上不會有程式一下是單選一下是複選?
    if (this.tableSelectionMode == TableSelectionMode.SINGLE) {
      this.systemMessageService.info(this.rowSelectedDatas.detailInput);
    } else {
      this.systemMessageService.info(this.rowSelectedDatas.map((data: ExampleDetail) => data.detailInput).toString());
    }

    // 很重要，自己決定要不要把記錄下來選擇的資料清空
    this.clearRowSelectedDatas();
  }

  override generateNewRowData(): ExampleDetail {
    let newRowData: ExampleDetail = new ExampleDetail();
    newRowData.id = DataAction.NEW;
    newRowData.uuid = uuidv4();
    return newRowData;
  }

  override async onPatchEditableRow(event: {data: ExampleDetail}): Promise<any> {
    console.log('patchEditableRow event = ' + JSON.stringify(event.data));

    this.inlineFormGroup.patchValue({
      id: event.data.id,
      uuid: event.data.uuid,
      detailInput: event.data.detailInput,
      detailCalendar: event.data.detailCalendar,
      detailDropdown: event.data.detailDropdown,
      detailAutoCompleteCode: event.data.detailAutoCompleteCode ? {
        "exampleDataCode": event.data.detailAutoCompleteCode,
        "exampleDataName": event.data.detailAutoCompleteName,
        "exampleDataNameTw": event.data.detailAutoCompleteNameTw,
        "exampleDataNameCn": event.data.detailAutoCompleteNameCn,
        "exampleDataNameUs": event.data.detailAutoCompleteNameUs
      } : null,
      detailInputNumber: event.data.detailInputNumber,
      detailCustValidation: event.data.detailCustValidation,
      detailDynamicDropdown: event.data.detailDynamicDropdown,
      detailAutoCompleteDropdownCode: event.data.detailAutoCompleteDropdownCode ? {
        "exampleDataCode": event.data.detailAutoCompleteDropdownCode,
        "exampleDataName": event.data.detailAutoCompleteDropdownName,
        "exampleDataNameTw": event.data.detailAutoCompleteDropdownNameTw,
        "exampleDataNameCn": event.data.detailAutoCompleteDropdownNameCn,
        "exampleDataNameUs": event.data.detailAutoCompleteDropdownNameUs
      } : null
    });
  }

  override async doSaveEditableRow(): Promise<boolean> {
    console.log('doSaveEditableRow tableFormGroup = ' + JSON.stringify(this.inlineFormGroup.getRawValue()));

    if (!this.inlineFormGroup.valid) {
      return false;
    }

    let savedData: ExampleDetail = new ExampleDetail();
    // 存檔後如果 id 是 new 表示新增的資料，把 id 清空，之後才不會被判斷成是新增未存檔的資料
    savedData.id =  this.inlineFormGroup.getRawValue().id != DataAction.NEW ? this.inlineFormGroup.getRawValue().id : '';
    savedData.uuid = this.inlineFormGroup.getRawValue().uuid;
    savedData.detailInput = this.inlineFormGroup.getRawValue().detailInput;
    savedData.detailCalendar = this.inlineFormGroup.getRawValue().detailCalendar;
    savedData.detailDropdown = this.inlineFormGroup.getRawValue().detailDropdown;
    savedData.detailAutoCompleteCode = this.inlineFormGroup.getRawValue().detailAutoCompleteCode?.exampleDataCode;
    savedData.detailAutoCompleteName = this.inlineFormGroup.getRawValue().detailAutoCompleteCode?.exampleDataName;
    savedData.detailAutoCompleteNameTw = this.inlineFormGroup.getRawValue().detailAutoCompleteCode?.exampleDataNameTw;
    savedData.detailAutoCompleteNameCn = this.inlineFormGroup.getRawValue().detailAutoCompleteCode?.exampleDataNameCn;
    savedData.detailAutoCompleteNameUs = this.inlineFormGroup.getRawValue().detailAutoCompleteCode?.exampleDataNameUs;
    savedData.setDetailAutoCompleteFilter();
    savedData.detailInputNumber = this.inlineFormGroup.getRawValue().detailInputNumber;
    savedData.detailCalendarRange = this.inlineFormGroup.getRawValue().detailCalendarRange;
    savedData.detailCustValidation = this.inlineFormGroup.getRawValue().detailCustValidation;
    savedData.detailDynamicDropdown = this.inlineFormGroup.getRawValue().detailDynamicDropdown;
    savedData.detailAutoCompleteDropdownCode = this.inlineFormGroup.getRawValue().detailAutoCompleteDropdownCode?.exampleDataCode;
    savedData.detailAutoCompleteDropdownName = this.inlineFormGroup.getRawValue().detailAutoCompleteDropdownCode?.exampleDataName;
    savedData.detailAutoCompleteDropdownNameTw = this.inlineFormGroup.getRawValue().detailAutoCompleteDropdownCode?.exampleDataNameTw;
    savedData.detailAutoCompleteDropdownNameCn = this.inlineFormGroup.getRawValue().detailAutoCompleteDropdownCode?.exampleDataNameCn;
    savedData.detailAutoCompleteDropdownNameUs = this.inlineFormGroup.getRawValue().detailAutoCompleteDropdownCode?.exampleDataNameUs;
    savedData.setDetailAutoCompleteDropdownFilter();

    // console.log('onRowEditSave = ' + JSON.stringify(savedData));

    const indexToUpdate = this.detailDatas.findIndex(data => data.uuid === savedData.uuid);
    this.detailDatas[indexToUpdate] = savedData;
    this.detailDatas = [...this.detailDatas];

    return true;
  }

  override onCancelEditableRow(): void {
    return;
  }

  detailCustValidator(): ValidatorFn {
    return (formControl: AbstractControl): ValidationErrors | null => {
      if (!formControl.parent) {
        return null;
      }

      // 欄位要做什麼驗證
      const error = formControl.parent?.get('detailCustValidation')?.value == '1';

      // 如果有錯
      if (error) {
        // 回傳 ValidationErrors，duplicate 是 error 的 key
        return { duplicate: true };
      }
      // 驗證結果符合需求，回傳 null
      return null;
    }
  }
}
